import React, { useCallback, useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Layout from './components/Shared/Layout';
import HomePage from './pages/HomePage';
import RconHackingPage from './pages/RconHackingPage';
import AccountChecker from './pages/AccountChecker';
import DdosTestingPage from './pages/DdosTestingPage';
import ServerDatabasePage from './pages/ServerDatabasePage';
import ServerDataExtractionPage from './pages/ServerDataExtractionPage';
import BotConnectionPage from './pages/BotConnectionPage';
import ZeroDayMethodPage from './pages/ZeroDayMethodPage';
import AccountPage from './pages/AccountPage';
import TermsPage from './pages/TermsPage';
import ModalWrapper from './components/Shared/ModalWrapper';
import ConsoleModal from './components/Shared/ConsoleModal';
import PaymentMethodsModal from './components/Shared/PaymentMethodsModal';
import SuccessModal from './components/Auth/SuccessModal';
import VerifyEmailModal from './components/Auth/VerifyEmailModal';
import ForgotPasswordModal from './components/Auth/ForgotPasswordModal';
import RegisterModal from './components/Auth/RegisterModal';
import LoginModal from './components/Auth/LoginModal';
import { WebSocketProvider } from './contexts/WebSocketContext';
import InsufficientPlanModal from './components/Shared/InsufficientPlanModal';
import AuthCallback from './pages/AuthCallback';
import PurchasePausedModal from './components/Shared/PurchasePausedModal'; // Added import
import MethodMaintenanceModal from './components/Shared/MethodMaintenanceModal';
import { useLanguage } from './contexts/LanguageContext'; // Import useLanguage
import { useAuth } from './contexts/AuthContext';
import MonitoringParserPage from './pages/MonitoringParserPage';
import LanguageDetector from './components/Shared/LanguageDetector';
import LanguageRedirect from './components/Shared/LanguageRedirect';
import LanguageRoutes from './components/Shared/LanguageRoutes';
import NewFeatureModal from './components/Shared/NewFeatureModal';

// Mock Data (For PaymentMethodsModal) - move or fetch as needed
const paymentMethodsData = [
  {
    id: 'card-1',
    name: 'Карты (1)',
    description: 'Visa, Mastercard, Мир',
    icon: 'fa-credit-card', // Standard credit card icon
    iconColor: 'text-accent-blue'
  },
  {
    id: 'card-2',
    name: 'Карты (2)',
    description: 'Альтернативный шлюз карт',
    icon: 'fa-credit-card', // Standard credit card icon
    iconColor: 'text-accent-blue'
  },
  // {
  //   id: 'card-3',
  //   name: 'Карты (3)',
  //   description: 'Еще один вариант карт',
  //   icon: 'fa-credit-card', // Standard credit card icon
  //   iconColor: 'text-accent-blue'
  // },
  {
    id: 'sbp',
    name: 'СБП',
    description: 'Система быстрых платежей',
    icon: 'fa-gem', // Closest to the diamond icon shown
    iconColor: 'text-accent-green'
  },
  {
    id: 'yoomoney', // Changed from 'ymoney' for clarity
    name: 'ЮMoney',
    description: 'Электронный кошелек',
    icon: 'fa-crown', // To match the crown icon shown
    iconColor: 'text-accent-yellow'
  },
  {
    id: 'crypto-1',
    name: 'Крипта (1)',
    description: 'BTC, ETH, USDT и др.',
    icon: 'fa-dollar-sign', // To match the dollar sign icon shown
    iconColor: 'text-accent-orange'
  },
  {
    id: 'contact_support',
    name: 'Другие способы оплаты',
    description: 'Криптовалюта, банк и др.',
    icon: 'fa-comments',
    iconColor: 'text-accent-blue',
    type: 'contact_support' // Специальный тип для идентификации
  }
  //   {
  //     id: 'crypto-2',
  //     name: 'Крипта (2)',
  //     description: 'Альтернативный криптошлюз',
  //     icon: 'fa-dollar-sign', // To match the dollar sign icon shown
  //     iconColor: 'text-accent-orange'
  //   },
  //  {
  //     id: 'skins',
  //     name: 'Скины',
  //     description: 'Оплата игровыми предметами',
  //     icon: 'fa-tshirt', // To match the t-shirt icon shown
  //     iconColor: 'text-accent-purple'
  //   }
  // Note: The "Другие способы оплаты" and "Назад" buttons from the image are
  // part of the overall UI structure, not individual payment methods in this list.
  // If you need a generic "Other methods" option like the one previously in your list:
  // {
  //   id: "other",
  //   name: 'Другие способы', // Or 'Other Methods'
  //   description: 'Свяжитесь с поддержкой', // Or 'Contact support'
  //   icon: 'fa-ellipsis-h', // Or 'fa-moon' to match the image's specific button
  //   iconColor: 'text-accent-pink'
  // }
  // However, based on the image, "Другие способы оплаты" seems to be a separate action button.
];

function App() {
  const [activeModal, setActiveModal] = useState(null); // null, 'console', 'payment'

  // Capture referral code from URL and clean it up (supports ?ref= and /r=)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refParam = params.get('ref');
    const path = window.location.pathname;

    // Regex to match /r=CODE or /en/r=CODE
    const pathRefMatch = path.match(/\/?(?:en\/)?r=([^/]+)/);

    if (refParam || pathRefMatch) {
      const code = refParam || pathRefMatch[1];
      console.log("Captured referral code:", code);
      localStorage.setItem('referralCode', code);

      let newPath = path;
      if (pathRefMatch) {
        // If it was a path-based ref, redirect to home
        newPath = path.startsWith('/en') ? '/en' : '/';
      }

      if (refParam) {
        params.delete('ref');
        const newSearch = params.toString();
        newPath = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      }

      window.history.replaceState({}, '', newPath);
      // Force a small delay and check if we need to help the router
      if (pathRefMatch) {
        window.location.href = newPath;
      }
    }
  }, []);

  const { user } = useAuth();

  // Background prefetch GTA Web as soon as the site loads
  useEffect(() => {
    // Small delay (2.5s) to guarantee zero impact on initial site rendering and animations
    const timer = setTimeout(() => {
      // 1. Prefetch index.html
      const docLink = document.createElement('link');
      docLink.rel = 'prefetch';
      docLink.as = 'document';
      docLink.href = '/games/gta/index.html';
      document.head.appendChild(docLink);

      // 2. Fetch index.html to find the hashed JS & CSS bundle and prefetch them into cache
      fetch('/games/gta/index.html')
        .then(res => res.text())
        .then(html => {
          const jsMatch = html.match(/src="(\.\/assets\/main-[^"]+\.js)"/);
          if (jsMatch && jsMatch[1]) {
            const jsUrl = '/games/gta/' + jsMatch[1].replace('./', '');
            const jsLink = document.createElement('link');
            jsLink.rel = 'prefetch';
            jsLink.as = 'script';
            jsLink.href = jsUrl;
            document.head.appendChild(jsLink);
          }
          const cssMatch = html.match(/href="(\.\/assets\/main-[^"]+\.css)"/);
          if (cssMatch && cssMatch[1]) {
            const cssUrl = '/games/gta/' + cssMatch[1].replace('./', '');
            const cssLink = document.createElement('link');
            cssLink.rel = 'prefetch';
            cssLink.as = 'style';
            cssLink.href = cssUrl;
            document.head.appendChild(cssLink);
          }
        })
        .catch(() => {});
    }, 2500);

    return () => clearTimeout(timer);
  }, [user]);

  const [modalData, setModalData] = useState({});
  const modalDataRef = useRef(modalData);
  useEffect(() => {
    modalDataRef.current = modalData;
  }, [modalData]);
  const { t } = useLanguage(); // Use translation hook

  const isClosingRef = useRef(false);
  const closeModal = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    const override = modalDataRef.current?.onCloseOverride;
    setActiveModal(null);
    setModalData({});
    document.body.classList.remove('overflow-hidden');
    if (override) {
      try {
        override();
      } catch (e) {
        console.error("Error in onCloseOverride:", e);
      }
    }
    isClosingRef.current = false;
  }, []);

  const openModal = useCallback((type, data = {}) => {
    if (!type) {
      closeModal();
      return;
    }
    console.log("Opening modal:", type, data);
    // Keep existing guards if needed
    if (type === 'login' || type === 'register') {
      console.warn("Attempted to open legacy auth modal:", type);
      return;
    }
    setModalData(data); // Store plan data here when opening 'payment'
    setActiveModal(type);
    document.body.classList.add('overflow-hidden');
  }, [closeModal]);

  // Determine maxWidth based on active modal type
  const getMaxWidth = () => {
    switch (activeModal) {
      case 'console': return modalData.maxWidth || 'max-w-3xl'; // Allow console to specify its width
      case 'payment': return 'max-w-xl';
      case 'planError': return 'max-w-lg'; // Adjust as needed
      case 'purchasePaused': return 'max-w-lg'; // Added for the new modal
      case 'methodMaintenance': return 'max-w-md'; // Added for the maintenance modal
      default: return 'max-w-lg'; // Default
    }
  };

  const renderModalContent = () => {
    switch (activeModal) {
      case 'payment':
        // Pass the plan data from modalData and paymentDetails if available
        return <PaymentMethodsModal
          methods={paymentMethodsData} // Or fetch dynamically
          plan={modalData.plan || {}} // Pass the plan stored in modalData
          actionType={modalData.actionType}
          paymentDetails={modalData.paymentDetails} // Pass payment details if received from backend status update
          onClose={closeModal}
          onConfirmWithMethod={modalData.onConfirmWithMethod} // <-- THIS IS THE FIX
          initialPhase={modalData.initialPhase} // Pass initialPhase
          statusMessage={modalData.statusMessage} // Pass statusMessage
        />;
      case 'console':
        // Console modal rendering remains the same
        return (
          <ConsoleModal
            title={modalData.title}
            lines={modalData.lines || []}
            isLoading={modalData.isLoading || false}
            status={modalData.status || "Idle"}
            statusColor={modalData.statusColor || "text-gray-400"}
            actions={modalData.actions || []}
            onClose={closeModal}
            maxWidth={modalData.maxWidth || 'max-w-3xl'}
            error={modalData.error} // Pass error prop
            isConsoleInputEnabled={modalData.isConsoleInputEnabled || false} // Pass it through
            onSendCommand={modalData.onSendCommand} // Pass it through
          // consoleMaxHeight is not set by ServerDatabasePage in modalData,
          // ConsoleModal will use its own default, which is fine.
          />
        );
      case 'planError':
        return <InsufficientPlanModal
          requiredPlan={modalData.requiredPlan || 'Higher Tier'}
          permission={modalData.permission}
          onClose={closeModal}
        />;
      case 'purchasePaused': // Added case for the new modal
        return <PurchasePausedModal onClose={closeModal} />;
      case 'methodMaintenance': // Added case for the maintenance modal
        return <MethodMaintenanceModal onClose={closeModal} />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-dark-800 text-gray-100 min-h-screen font-sans">
      {/* Adjust max-width specifically for payment modal */}
      <Router>
        <LanguageDetector />
        <LanguageRedirect />
        <NewFeatureModal />
        <ModalWrapper
          isOpen={activeModal !== null}
          onClose={closeModal}
          maxWidth={getMaxWidth()}
          plain={activeModal === 'console'}
          contentPadding={activeModal === 'payment' ? 'p-3.5 sm:p-4' : undefined}
        >
          {renderModalContent()}
        </ModalWrapper>
        {/* Pass openModal and closeModal down */}
        <Layout openModal={openModal}>
          <LanguageRoutes openModal={openModal} closeModal={closeModal} />
        </Layout>
      </Router>
    </div>
  );
}

export default App;